import {
  getGroupIdFromAccessId,
  isGroupAccessId,
  isStudentAccessId,
} from "../../../../shared/access-id";
import type { SectionUserActivityType } from "../../../../shared/types/attendance";

import { isUserOnline } from "components/materials/section/helpers";
import type { PageGroupUserType, PageType } from "components/server-types";
import { SectionUserActivityStatus, determineActivityStatus } from "store/selectors";
import type {
  ExtendedPageGroupUserType,
  NestedPageGroupType,
  UserWithSectionActivity,
} from "store/selectors/types";
import { filterMap } from "utils/collections";
import { isGroupPage, isIndividualPage } from "utils/page-utils";
import { ModelType } from "utils/type-utils";

export const hasTotalUsersReachedMaxGroupSize = (pageGroup: NestedPageGroupType, page: PageType) =>
  page.max_group_size !== null && pageGroup.page_group_users.length >= page.max_group_size;

export const hasReadiedUsersReachedMaxGroupSize = (
  pageGroup: NestedPageGroupType,
  page: PageType,
) =>
  page.max_group_size !== null &&
  pageGroup.page_group_users.filter((pageGroupUser) => pageGroupUser.is_ready).length >=
    page.max_group_size;

export const isValidAccessIdForPageType = (page: PageType, accessId: string) =>
  page &&
  ((isGroupPage(page) && isGroupAccessId(accessId)) ||
    (isIndividualPage(page) && isStudentAccessId(accessId)));

export const showPageDisabled = (page: PageType) =>
  !page.released_at && page.category !== "instructor_workspace";

export const isPageGroupUserInPageGroup = (
  pageGroupUser: PageGroupUserType | null,
  pageGroup: ModelType,
) => pageGroupUser && pageGroupUser.page_group_id === pageGroup.id;

export type DraggableSectionUserType = UserWithSectionActivity & {
  order: number;
  page_group_id?: string;
};

export type DraggablePageGroupUserType = ExtendedPageGroupUserType & {
  order: number;
};

export const findPageGroupIndexOfStudentUser = (
  pageGroups: NestedPageGroupType[],
  sectionUserId: string | null,
) =>
  pageGroups.findIndex(
    (pageGroup) =>
      sectionUserId &&
      pageGroup.page_group_users.find(
        (pageGroupUser) =>
          pageGroupUser.section_user_id === sectionUserId && pageGroupUser.is_ready,
      ),
  );

export const findPageGroupIndexOfAccessId = (
  pageGroups: NestedPageGroupType[],
  accessId: string | undefined | null,
) => {
  if (!accessId || !isGroupAccessId(accessId)) {
    return -1;
  }
  const groupId = getGroupIdFromAccessId(accessId);
  return pageGroups.findIndex((pageGroup) => pageGroup.id === groupId);
};

/** Stable fingerprint of who is in which page group; used to invalidate local ungrouped overrides. */
export const getPageGroupsMembershipHash = (
  pageGroups: NestedPageGroupType[],
  excludeSectionUserId?: string,
) => {
  const membership: string[] = [];
  for (const pageGroup of pageGroups) {
    for (const pageGroupUser of pageGroup.page_group_users) {
      if (excludeSectionUserId == null || pageGroupUser.section_user_id !== excludeSectionUserId) {
        membership.push(`${pageGroup.id}:${pageGroupUser.section_user_id}`);
      }
    }
  }
  return membership.sort().join("|");
};

export const getUngroupedStudentUsers = (
  pageGroups: NestedPageGroupType[],
  studentUsers: UserWithSectionActivity[],
): DraggableSectionUserType[] => {
  const pageGroupUserIds = pageGroups.flatMap((pageGroup) =>
    filterMap(
      pageGroup.page_group_users,
      (pageGroupUser) => pageGroupUser.is_ready,
      (pageGroupUser) => pageGroupUser.section_user_id,
    ),
  );

  const pageGroupUserIdSet = new Set(pageGroupUserIds);

  return filterMap(
    studentUsers,
    (user) => !pageGroupUserIdSet.has(user.section_user_id),
    (user, index) => ({ ...user, order: index }),
  );
};

export const atLeastOneStudentNotInGroup = (
  pageGroups: NestedPageGroupType[],
  studentUsers: UserWithSectionActivity[],
  userActivityStatus: Record<string, SectionUserActivityType>,
) => {
  const ungroupedStudentUsers = getUngroupedStudentUsers(pageGroups, studentUsers);
  const atLeastOneUngroupedOnlineStudent =
    ungroupedStudentUsers.filter((studentUser) => isUserOnline(studentUser.sectionActivityStatus))
      .length > 0;
  const atLeastOneStudentInGroup = ungroupedStudentUsers.length !== studentUsers.length;
  const atLeastOneUnreadyActiveStudent = pageGroups.some((pageGroup) =>
    pageGroup.page_group_users.find(
      (pageGroupUser) =>
        !pageGroupUser.is_ready &&
        determineActivityStatus(userActivityStatus[pageGroupUser.user_id]) ===
          SectionUserActivityStatus.ACTIVE,
    ),
  );

  return (
    (atLeastOneUngroupedOnlineStudent && atLeastOneStudentInGroup) || atLeastOneUnreadyActiveStudent
  );
};
